import { c as _c } from "react/compiler-runtime";
import { ServiceDetailsModal } from "../steps/services-step/service-details-modal";
import { addMinutes } from "date-fns";
import { useMemo, useState } from "react";
import { FaCalendarAlt, FaMapMarkerAlt, FaUserMd, FaUserNurse } from "react-icons/fa";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { IconType } from "react-icons/lib";
import { LuX } from "react-icons/lu";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { usePatientClaim } from "@/hooks/patient-claim/use-patient-claim";
import { formatAddress, formatCurrency, formatDateWithDay, formatStaffName, formatTime, formatToHumanReadable } from "@/lib/format-data";
import { type DurationBound, atDurationBound, formatDurationRange } from "@/lib/provider-service-duration";
import { VIRTUAL_VISIT_DETAIL, formatVisitTime } from "@/lib/self-schedule-virtual-services";
import { computeTotalDuration, computeTotalPriceRange, flattenSelectedServices, hasAnyPriceRange, resolveServiceOccurrences } from "@/lib/service-addon-utils";
import { FeeStructure, computeBookingFeeCreditCents, computeTotalBookingFeeCents, getCancellationFeeInfo } from "@/lib/service-fees";
import { tv } from "@/lib/tailwind-utils";
import { cx } from "@/lib/tailwind-utils";
import { useStoreTimezone } from "@/lib/timezone";
import { PublicProviderSummary } from "@/types/provider";
import { PublicServiceDetail } from "@/types/service";
import { getPrepaidCoverageByService, getPrepaidCoverageCopy } from "./prepaid-coverage";
import { usePatientSavedItemBenefits } from "@/api/patient/portal-benefits-query";
import { FIRST_AVAILABLE_PROVIDER_ID, SelfScheduleStep, useSelfScheduleStore } from "@/stores/self-schedule-store";
const itinerary = tv({
  slots: {
    root: "space-y-2 rounded-xl opacity-100 transition-all duration-300",
    heading: "leading-none tracking-wide",
    divider: "divide-y",
    item: "motion-preset-slide-left-sm py-4",
    iconWrap: "",
    icon: "",
    body: "",
    label: "font-medium",
    title: "font-semibold leading-tight",
    subtitle: "text-foreground-muted text-caption"
  },
  variants: {
    confirmation: {
      true: {
        root: "text-primary-foreground md:text-foreground",
        heading: "text-primary-foreground md:text-foreground",
        divider: "divide-primary-foreground/20 md:divide-foreground/20",
        item: "flex items-start gap-3",
        iconWrap: "flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-full bg-foreground/5 mt-1 text-foreground-muted md:text-inherit",
        icon: "h-4 w-4 text-primary-foreground/80 md:text-inherit",
        body: "flex-1 space-y-0.5",
        label: "text-overline text-primary-foreground/80 md:text-inherit",
        title: "text-body text-primary-foreground md:text-foreground",
        subtitle: "text-caption text-primary-foreground/80 md:text-foreground-secondary"
      },
      false: {
        root: "text-primary-foreground",
        heading: "text-primary-foreground",
        divider: "divide-primary-foreground/20",
        item: "space-y-1.5",
        iconWrap: "flex items-center gap-2 text-foreground-muted",
        icon: "h-3 w-3 text-primary-foreground/80",
        body: "space-y-1.5",
        label: "text-caption text-primary-foreground/80",
        title: "text-body text-primary-foreground",
        subtitle: "text-caption text-primary-foreground/80"
      }
    }
  }
});
type Styles = ReturnType<typeof itinerary>;

// Shown the way the provider chose, credentials included if they turned them on.
const formatProviderLabel = (provider: PublicProviderSummary) => formatStaffName(provider);
const Header = t0 => {
  const $ = _c(15);
  const {
    icon: Icon,
    label,
    styles,
    showLabel
  } = t0;
  let t1;
  if ($[0] !== styles) {
    t1 = styles.iconWrap();
    $[0] = styles;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== styles) {
    t2 = styles.icon();
    $[2] = styles;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== Icon || $[5] !== t2) {
    t3 = <Icon className={t2} />;
    $[4] = Icon;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== label || $[8] !== showLabel || $[9] !== styles) {
    t4 = showLabel && <p className={styles.label()}>{label}</p>;
    $[7] = label;
    $[8] = showLabel;
    $[9] = styles;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t3 || $[13] !== t4) {
    t5 = <div className={t1}>{t3}{t4}</div>;
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
export const Itinerary = t0 => {
  const $ = _c(115);
  const {
    className,
    showItineraryTitle: t1
  } = t0;
  const showItineraryTitle = t1 === undefined ? true : t1;
  const {
    selectedServices,
    selectedProvider,
    selectedAvailabilityAssignments,
    selectedAvailabilityProviders,
    selectedAvailabilityProvider,
    selectedLocation,
    isVirtualServiceBooking,
    bookingLocation,
    selectedDateTime,
    locationType,
    hasData,
    currentStep,
    showServiceDuration,
    bookingFeeMode,
    isRwgLanding,
    isMultiProviderSelection,
    usesPerOccurrenceProviderSelection,
    providerGroups,
    serviceInstanceProviderIds
  } = useSelfScheduleContext();
  const {
    isMobile
  } = useIsMobile();
  const {
    selectedAddons,
    selectedServiceInstances,
    removeSelectedService,
    selectedAvailabilityWindow
  } = useSelfScheduleStore();
  const [showServiceDetailsModal, setShowServiceDetailsModal] = useState();
  const isConfirmation = currentStep === SelfScheduleStep.CONFIRMATION;
  const pageTimezone = useStoreTimezone();
  const {
    claimedPatient
  } = usePatientClaim();
  let t2;
  if ($[0] !== isConfirmation) {
    t2 = {
      enabled: isConfirmation
    };
    $[0] = isConfirmation;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: t3
  } = usePatientSavedItemBenefits(claimedPatient?.id ?? null, t2);
  let t4;
  if ($[2] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const savedItemBenefits = t4;
  let t5;
  bb0: {
    if (!selectedServices) {
      let t6;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = [];
        $[4] = t6;
      } else {
        t6 = $[4];
      }
      t5 = t6;
      break bb0;
    }
    let t6;
    if ($[5] !== selectedServiceInstances || $[6] !== selectedServices) {
      const nameById = new Map(selectedServices.map(_temp));
      const groupMap = new Map();
      for (const inst of selectedServiceInstances) {
        const name = nameById.get(inst.id) ?? inst.name;
        if (!name) {
          continue;
        }
        const existing = groupMap.get(inst.id);
        if (existing) {
          existing.count = existing.count + 1;
          existing.instanceIds.push(inst.instanceId);
        } else {
          groupMap.set(inst.id, {
            serviceId: inst.id,
            name,
            count: 1,
            instanceIds: [inst.instanceId]
          });
        }
      }
      t6 = Array.from(groupMap.values());
      $[5] = selectedServiceInstances;
      $[6] = selectedServices;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    t5 = t6;
  }
  const serviceGroups = t5;
  const canRemoveInstances = currentStep !== SelfScheduleStep.CONFIRMATION && currentStep !== SelfScheduleStep.PAYMENT_METHOD && currentStep !== SelfScheduleStep.SECURE_APPOINTMENT && currentStep !== SelfScheduleStep.CHECKOUT && currentStep !== SelfScheduleStep.BOOKING_COMPLETE;
  let t6;
  if ($[8] !== selectedServices) {
    t6 = selectedServices ?? [];
    $[8] = selectedServices;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== selectedAddons || $[11] !== t6) {
    t7 = flattenSelectedServices(t6, selectedAddons);
    $[10] = selectedAddons;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const feeServices = t7;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t8;
  let t9;
  if ($[13] !== bookingFeeMode || $[14] !== bookingLocation || $[15] !== canRemoveInstances || $[16] !== className || $[17] !== feeServices || $[18] !== hasData || $[19] !== isConfirmation || $[20] !== isMobile || $[21] !== isMultiProviderSelection || $[22] !== isRwgLanding || $[23] !== isVirtualServiceBooking || $[24] !== locationType || $[25] !== pageTimezone || $[26] !== providerGroups || $[27] !== removeSelectedService || $[28] !== savedItemBenefits || $[29] !== selectedAddons || $[30] !== selectedAvailabilityAssignments || $[31] !== selectedAvailabilityProvider || $[32] !== selectedAvailabilityProviders || $[33] !== selectedAvailabilityWindow || $[34] !== selectedDateTime || $[35] !== selectedLocation || $[36] !== selectedProvider || $[37] !== selectedServiceInstances || $[38] !== selectedServices || $[39] !== serviceGroups || $[40] !== serviceInstanceProviderIds || $[41] !== showItineraryTitle || $[42] !== showServiceDuration || $[43] !== usesPerOccurrenceProviderSelection) {
    const bookingFee = computeTotalBookingFeeCents(feeServices, bookingFeeMode);
    const bookingFeeCreditAmount = computeBookingFeeCreditCents(feeServices, bookingFeeMode);
    const fullBookingFeeAppliesAsCredit = !!bookingFee && bookingFeeCreditAmount === bookingFee;
    let t17;
    if ($[53] !== selectedServices) {
      const cancellationFees = selectedServices?.flatMap(_temp2);
      t17 = cancellationFees?.reduce(_temp3, 0);
      $[53] = selectedServices;
      $[54] = t17;
    } else {
      t17 = $[54];
    }
    const totalCancellationFees = t17;
    let t18;
    if ($[55] !== selectedAvailabilityWindow || $[56] !== selectedDateTime) {
      t18 = selectedDateTime && selectedAvailabilityWindow && new Date(selectedAvailabilityWindow.start).getTime() === selectedDateTime.getTime() ? (new Date(selectedAvailabilityWindow.end).getTime() - new Date(selectedAvailabilityWindow.start).getTime()) / 60000 : undefined;
      $[55] = selectedAvailabilityWindow;
      $[56] = selectedDateTime;
      $[57] = t18;
    } else {
      t18 = $[57];
    }
    const slotMinutes = t18;
    let t19;
    if ($[58] !== selectedAddons || $[59] !== selectedServices) {
      t19 = bound => computeTotalDuration((selectedServices ?? []).map(service_0 => atDurationBound(service_0, bound)), selectedAddons);
      $[58] = selectedAddons;
      $[59] = selectedServices;
      $[60] = t19;
    } else {
      t19 = $[60];
    }
    const totalAt = t19;
    const totalDuration = selectedServices ? slotMinutes ?? totalAt("min") : undefined;
    const totalDurationLabel = slotMinutes != null || !selectedServices ? `${totalDuration}` : formatDurationRange(totalAt("min"), totalAt("max"));
    const totalPriceRange = selectedServices ? computeTotalPriceRange(selectedServices, selectedAddons) : undefined;
    let t20;
    if ($[61] !== selectedAddons || $[62] !== selectedServices) {
      t20 = selectedServices ? hasAnyPriceRange(selectedServices, selectedAddons) : undefined;
      $[61] = selectedAddons;
      $[62] = selectedServices;
      $[63] = t20;
    } else {
      t20 = $[63];
    }
    const priceFlags = t20;
    const formatTotalPrice = () => {
      if (!totalPriceRange || totalPriceRange.min <= 0) {
        return null;
      }
      if (priceFlags?.isRange && totalPriceRange.max > totalPriceRange.min) {
        return `${formatCurrency(totalPriceRange.min)} - ${formatCurrency(totalPriceRange.max)}`;
      }
      if (priceFlags?.isStartsAt) {
        return `${formatCurrency(totalPriceRange.min)}+`;
      }
      if (priceFlags?.isEstimated) {
        return `from ${formatCurrency(totalPriceRange.min)}`;
      }
      return formatCurrency(totalPriceRange.min);
    };
    const endTime = selectedDateTime ? addMinutes(selectedDateTime, totalDuration ?? 0) : undefined;
    let t21;
    if ($[64] !== selectedServices) {
      t21 = !!selectedServices?.length && selectedServices.every(_temp4);
      $[64] = selectedServices;
      $[65] = t21;
    } else {
      t21 = $[65];
    }
    const allServicesAllowProviderSelection = t21;
    let t22;
    if ($[66] !== allServicesAllowProviderSelection || $[67] !== selectedAvailabilityAssignments || $[68] !== selectedAvailabilityProviders || $[69] !== selectedDateTime) {
      t22 = !!selectedDateTime && allServicesAllowProviderSelection && selectedAvailabilityAssignments.length > 0 && selectedAvailabilityAssignments.every((_, index) => !!selectedAvailabilityProviders[index]);
      $[66] = allServicesAllowProviderSelection;
      $[67] = selectedAvailabilityAssignments;
      $[68] = selectedAvailabilityProviders;
      $[69] = selectedDateTime;
      $[70] = t22;
    } else {
      t22 = $[70];
    }
    const showMaterializedProviderAssignments = t22;
    const showInlineServiceProviders = showMaterializedProviderAssignments || (usesPerOccurrenceProviderSelection || isMultiProviderSelection) && providerGroups.length > 0;
    let t23;
    bb1: {
      const labels = new Map();
      const add = (serviceId, label) => {
        const existing_0 = labels.get(serviceId);
        if (!existing_0) {
          labels.set(serviceId, [label]);
        } else {
          if (!existing_0.includes(label)) {
            existing_0.push(label);
          }
        }
      };
      if (showMaterializedProviderAssignments) {
        selectedAvailabilityAssignments.forEach((assignment, index_0) => {
          const provider = selectedAvailabilityProviders[index_0];
          if (provider) {
            add(assignment.serviceId, formatProviderLabel(provider));
          }
        });
        t23 = labels;
        break bb1;
      }
      if (usesPerOccurrenceProviderSelection || isMultiProviderSelection) {
        for (const group of providerGroups) {
          for (const instance of group.instances) {
            const providerId = serviceInstanceProviderIds[instance.instanceId];
            const provider_0 = group.eligibleProviders.find(candidate => candidate.id === providerId);
            if (provider_0) {
              add(instance.id, formatProviderLabel(provider_0));
            } else {
              if (providerId === FIRST_AVAILABLE_PROVIDER_ID) {
                add(instance.id, "First available");
              }
            }
          }
        }
      }
      t23 = labels;
    }
    const providerLabelsByServiceId = t23;
    let t24;
    bb2: {
      if (!isConfirmation) {
        t24 = {};
        break bb2;
      }
      t24 = getPrepaidCoverageByService({
        selectedServiceInstances,
        selectedServices,
        savedItems: savedItemBenefits
      });
    }
    const prepaidCoverageByService = t24;
    const itineraryProvider = !showInlineServiceProviders ? selectedProvider ?? (allServicesAllowProviderSelection ? selectedAvailabilityProvider : undefined) : undefined;
    let styles;
    if ($[71] !== className || $[72] !== hasData || $[73] !== isConfirmation || $[74] !== isVirtualServiceBooking || $[75] !== locationType || $[76] !== selectedLocation || $[77] !== showItineraryTitle) {
      styles = itinerary({
        confirmation: isConfirmation
      });
      t15 = cx(styles.root(), className);
      t16 = hasData && showItineraryTitle && <h3 className={styles.heading()}>Your Itinerary</h3>;
      t8 = styles.divider();
      t9 = locationType && !selectedLocation && !isVirtualServiceBooking && <div className={styles.item()}><Header icon={FaMapMarkerAlt} label="Location" styles={styles} showLabel={!isConfirmation} /><div className={styles.body()}>{isConfirmation && <p className={styles.label()}>Location</p>}<h4 className={styles.title()}>{formatToHumanReadable(locationType)} Location</h4></div></div>;
      $[71] = className;
      $[72] = hasData;
      $[73] = isConfirmation;
      $[74] = isVirtualServiceBooking;
      $[75] = locationType;
      $[76] = selectedLocation;
      $[77] = showItineraryTitle;
      $[78] = styles;
      $[79] = t15;
      $[80] = t16;
      $[81] = t8;
      $[82] = t9;
    } else {
      styles = $[78];
      t15 = $[79];
      t16 = $[80];
      t8 = $[81];
      t9 = $[82];
    }
    t10 = !!selectedServices?.length && <div className={styles.item()}><div onClick={() => {
        setShowServiceDetailsModal(selectedServices);
      }}><Header icon={FaUserNurse} label="Service" styles={styles} showLabel={!isConfirmation} /></div><div className={styles.body()}>{isConfirmation && <p className={styles.label()}>Service</p>}<ul className="space-y-1">{serviceGroups.map(group_0 => {
            const service_2 = selectedServices.find(s_0 => s_0.id === group_0.serviceId);
            const addonOccurrences = new Map();
            if (service_2) {
              const occurrences = resolveServiceOccurrences(selectedServiceInstances.filter(inst_0 => inst_0.id === group_0.serviceId).map(() => service_2), selectedAddons);
              for (const {
                addons: selected
              } of occurrences) {
                for (const addon of selected) {
                  const existing_1 = addonOccurrences.get(addon.id);
                  addonOccurrences.set(addon.id, {
                    addon,
                    count: (existing_1?.count ?? 0) + 1
                  });
                }
              }
            }
            const addons = Array.from(addonOccurrences.values());
            const providerLabels = providerLabelsByServiceId.get(group_0.serviceId) ?? [];
            const prepaidCoverageCopy = getPrepaidCoverageCopy(prepaidCoverageByService[group_0.serviceId] ?? {
              selectedCount: group_0.count,
              coveredCount: 0
            });
            return <li key={group_0.serviceId} className="flex items-start justify-between gap-2"><div className="min-w-0 flex-1"><h4 className={cx(styles.title(), "cursor-pointer")} onClick={() => {
                  if (service_2) {
                    setShowServiceDetailsModal([service_2]);
                  }
                }}>{group_0.name}{group_0.count > 1 && <span className="ml-1 text-xs font-semibold opacity-80">× {group_0.count}</span>}</h4>{providerLabels.length > 0 && <p className={styles.subtitle()}>with {providerLabels.join(" & ")}</p>}{addons.length > 0 && <ul className={cx(styles.subtitle(), "mt-1 space-y-0.5")}>{addons.map(t25 => {
                    const {
                      addon: addon_0,
                      count
                    } = t25;
                    const addonProviderLabels = providerLabelsByServiceId.get(addon_0.addonService.id) ?? [];
                    const extraProviderLabels = addonProviderLabels.filter(label_0 => !providerLabels.includes(label_0));
                    return <li key={addon_0.id}><span>{`+ ${addon_0.addonService.name}${count > 1 ? ` × ${count}` : ""}${extraProviderLabels.length > 0 ? ` with ${extraProviderLabels.join(" & ")}` : ""}`}</span></li>;
                  })}</ul>}{isConfirmation && prepaidCoverageCopy && <p className={cx("mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-caption font-medium", "bg-status-emerald-bg text-status-emerald-text")}><IoCheckmarkCircleOutline className="h-3.5 w-3.5 shrink-0" /><span>{prepaidCoverageCopy}</span></p>}</div>{canRemoveInstances && group_0.instanceIds.length > 0 && <button type="button" aria-label={group_0.count > 1 ? `Remove one ${group_0.name}` : `Remove ${group_0.name}`} onClick={e => {
                e.stopPropagation();
                const last = group_0.instanceIds[group_0.instanceIds.length - 1];
                removeSelectedService(last);
              }} className={cx("flex items-center justify-center rounded-full", "h-6 w-6 mt-0.5", "text-foreground-muted hover:text-foreground hover:bg-foreground/10", "transition-colors")}><LuX className="h-3.5 w-3.5" /></button>}</li>;
          })}</ul>{(isConfirmation || isMobile) && <div className="space-y-0.5 text-body-lg">{showServiceDuration && <p className={styles.label()}><span>Lasts {totalDurationLabel} minutes</span></p>}{formatTotalPrice() && <p className={styles.label()}><span>Costs {formatTotalPrice()}</span></p>}{!!totalCancellationFees && <p className={styles.label()}><span>Cancellation Fees Apply.</span></p>}{!!bookingFee && !isRwgLanding && <p className={styles.label()}><span>{fullBookingFeeAppliesAsCredit ? <>{formatCurrency(bookingFee)} deposit, credited toward your appointment after payment.</> : <>{formatCurrency(bookingFee)} booking fee.{bookingFeeCreditAmount > 0 && <>{" "}{formatCurrency(bookingFeeCreditAmount)} of this booking fee becomes a credit you can use toward your appointment after payment.</>}</>}</span></p>}<button type="button" className={cx("pt-1 !text-caption cursor-pointer text-left", styles.title())} onClick={() => {
            setShowServiceDetailsModal(selectedServices);
          }}>Click for more details.</button></div>}</div></div>;
    if ($[83] !== isConfirmation || $[84] !== itineraryProvider || $[85] !== showInlineServiceProviders || $[86] !== styles) {
      t11 = !showInlineServiceProviders && itineraryProvider && <div className={styles.item()}><Header icon={FaUserMd} label="Provider" styles={styles} showLabel={!isConfirmation} /><div className={styles.body()}>{isConfirmation && <p className={styles.label()}>Provider</p>}<h4 className={styles.title()}>{formatStaffName(itineraryProvider)}</h4></div></div>;
      $[83] = isConfirmation;
      $[84] = itineraryProvider;
      $[85] = showInlineServiceProviders;
      $[86] = styles;
      $[87] = t11;
    } else {
      t11 = $[87];
    }
    if ($[88] !== isConfirmation || $[89] !== isVirtualServiceBooking || $[90] !== styles) {
      t12 = isVirtualServiceBooking && <div className={styles.item()}><Header icon={FaMapMarkerAlt} label="Location" styles={styles} showLabel={!isConfirmation} /><div className={styles.body()}>{isConfirmation && <p className={styles.label()}>Location</p>}<h4 className={styles.title()}>Telehealth visit</h4><span className={styles.label()}>{VIRTUAL_VISIT_DETAIL}</span></div></div>;
      $[88] = isConfirmation;
      $[89] = isVirtualServiceBooking;
      $[90] = styles;
      $[91] = t12;
    } else {
      t12 = $[91];
    }
    if ($[92] !== isConfirmation || $[93] !== isVirtualServiceBooking || $[94] !== selectedLocation || $[95] !== styles) {
      t13 = selectedLocation && !isVirtualServiceBooking && <div className={styles.item()}><Header icon={FaMapMarkerAlt} label="Location" styles={styles} showLabel={!isConfirmation} /><div className={styles.body()}>{isConfirmation && <p className={styles.label()}>Location</p>}<h4 className={styles.title()}>{selectedLocation.name}</h4><span>{selectedLocation.isVirtual ? "Telehealth appointment" : formatAddress(selectedLocation.addressLineOne, selectedLocation.city, selectedLocation.state, selectedLocation.zipCode, selectedLocation.addressLineTwo, styles.label())}</span></div></div>;
      $[92] = isConfirmation;
      $[93] = isVirtualServiceBooking;
      $[94] = selectedLocation;
      $[95] = styles;
      $[96] = t13;
    } else {
      t13 = $[96];
    }
    t14 = selectedDateTime && <div className={styles.item()}><Header icon={FaCalendarAlt} label="Date & Time" styles={styles} showLabel={!isConfirmation} /><div className={styles.body()}>{isConfirmation && <p className={styles.label()}>{"Date & Time"}</p>}<h4 className={styles.title()}>{formatDateWithDay(selectedDateTime)}</h4><div className={styles.label()}>{isVirtualServiceBooking && bookingLocation ? <span>{formatVisitTime(selectedDateTime, bookingLocation.timezone, pageTimezone)}</span> : <span>{formatTime(selectedDateTime)}{showServiceDuration && <>{" "}- {!!selectedServices?.length && formatTime(endTime)}</>}</span>}</div></div></div>;
    $[13] = bookingFeeMode;
    $[14] = bookingLocation;
    $[15] = canRemoveInstances;
    $[16] = className;
    $[17] = feeServices;
    $[18] = hasData;
    $[19] = isConfirmation;
    $[20] = isMobile;
    $[21] = isMultiProviderSelection;
    $[22] = isRwgLanding;
    $[23] = isVirtualServiceBooking;
    $[24] = locationType;
    $[25] = pageTimezone;
    $[26] = providerGroups;
    $[27] = removeSelectedService;
    $[28] = savedItemBenefits;
    $[29] = selectedAddons;
    $[30] = selectedAvailabilityAssignments;
    $[31] = selectedAvailabilityProvider;
    $[32] = selectedAvailabilityProviders;
    $[33] = selectedAvailabilityWindow;
    $[34] = selectedDateTime;
    $[35] = selectedLocation;
    $[36] = selectedProvider;
    $[37] = selectedServiceInstances;
    $[38] = selectedServices;
    $[39] = serviceGroups;
    $[40] = serviceInstanceProviderIds;
    $[41] = showItineraryTitle;
    $[42] = showServiceDuration;
    $[43] = usesPerOccurrenceProviderSelection;
    $[44] = t10;
    $[45] = t11;
    $[46] = t12;
    $[47] = t13;
    $[48] = t14;
    $[49] = t15;
    $[50] = t16;
    $[51] = t8;
    $[52] = t9;
  } else {
    t10 = $[44];
    t11 = $[45];
    t12 = $[46];
    t13 = $[47];
    t14 = $[48];
    t15 = $[49];
    t16 = $[50];
    t8 = $[51];
    t9 = $[52];
  }
  let t17;
  if ($[97] !== t10 || $[98] !== t11 || $[99] !== t12 || $[100] !== t13 || $[101] !== t14 || $[102] !== t8 || $[103] !== t9) {
    t17 = <div className={t8}>{t9}{t10}{t11}{t12}{t13}{t14}</div>;
    $[97] = t10;
    $[98] = t11;
    $[99] = t12;
    $[100] = t13;
    $[101] = t14;
    $[102] = t8;
    $[103] = t9;
    $[104] = t17;
  } else {
    t17 = $[104];
  }
  const t18 = showServiceDetailsModal !== undefined;
  let t19;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setShowServiceDetailsModal(undefined);
    $[105] = t19;
  } else {
    t19 = $[105];
  }
  let t20;
  if ($[106] !== showServiceDetailsModal || $[107] !== showServiceDuration || $[108] !== t18) {
    t20 = <ServiceDetailsModal services={showServiceDetailsModal} showDuration={showServiceDuration} isOpen={t18} onClose={t19} />;
    $[106] = showServiceDetailsModal;
    $[107] = showServiceDuration;
    $[108] = t18;
    $[109] = t20;
  } else {
    t20 = $[109];
  }
  let t21;
  if ($[110] !== t15 || $[111] !== t16 || $[112] !== t17 || $[113] !== t20) {
    t21 = <div className={t15}>{t16}{t17}{t20}</div>;
    $[110] = t15;
    $[111] = t16;
    $[112] = t17;
    $[113] = t20;
    $[114] = t21;
  } else {
    t21 = $[114];
  }
  return t21;
};
function _temp(s) {
  return [s.id, s.name];
}
function _temp2(service) {
  return getCancellationFeeInfo(service);
}
function _temp3(acc, fee) {
  return acc + (fee.amountCents ?? 0);
}
function _temp4(service_1) {
  return service_1.allowIndividualProviderBooking === true;
}